import { useEffect, useRef, useState } from "react";

import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";
import { UsersService } from "@services/UsersService";
import { Modal } from "@shared/designSystem/messages/Modal";

import ExportNovedadesCreateScreen from "./ExportNovedadesCreateScreen";
import ExportNovedadesDetailScreen from "./ExportNovedadesDetailScreen";
import { VerticalContainer } from "@shared/designSystem/Containers";
import { HollowedButton } from "@shared/designSystem/Button";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { MilestoneSteps } from "@shared/designSystem/MilestoneSteps";
import { Text } from "@shared/designSystem/Texts";

export type ExportNovedadesModalMode = "new" | "view";

interface ExportNovedadesModalScreenProps {
    isOpen: boolean;
    mode: ExportNovedadesModalMode;
    orgID: string;
    service: NovedadesExportService;
    usersService: UsersService;
    process?: NovedadesExportServiceModel.ProcessRecord;
    onClose: () => void;
    onCreated?: (process: NovedadesExportServiceModel.ProcessRecord) => Promise<void> | void;
    onProcessUpdated?: () => Promise<void> | void;
}

const titleByMode: Record<ExportNovedadesModalMode, string> = {
    new: "Nueva exportación",
    view: "Detalle de exportación",
};

const isDetailReadyStatus = (
    process: NovedadesExportServiceModel.ProcessRecord | undefined,
): boolean => {
    const status = process?.status;
    const isFileGenerationStep = (
        process?.currentStep === "PREPARING_EXPORT_DATA" ||
        process?.currentStep === "DATA_VALIDATION" ||
        process?.currentStep === "FILE_GENERATION" ||
        process?.currentStep === "FILE_CREATED"
    );
    return (
        status === "READY_FOR_REVIEW" ||
        status === "EXPORT_GENERATED" ||
        status === "COMPLETED" ||
        status === "PARTIALLY_FAILED" ||
        (status === "RUNNING" && isFileGenerationStep)
    );
};

const isPollingCompleteStatus = (status: string | undefined): boolean => {
    return (
        status === "READY_FOR_REVIEW" ||
        status === "EXPORT_GENERATED" ||
        status === "COMPLETED" ||
        status === "PARTIALLY_FAILED" ||
        status === "FAILED"
    );
};

const processAnalyticsPayload = (processRecord: NovedadesExportServiceModel.ProcessRecord) => ({
    processID: processRecord.processID,
    status: processRecord.status,
    currentStep: processRecord.currentStep,
    exportSystem: processRecord.exportSystem,
    employeeCount: processRecord.employeeCount ?? 0,
    processedEmployeeCount: processRecord.processedEmployeeCount ?? 0,
    errorsCount: processRecord.errorsCount ?? 0,
    warningsCount: processRecord.warningsCount ?? 0,
});

const loadingTextByStep: Record<string, string> = {
    EMPLOYEE_SCOPE: "Preparando la selección de colaboradores...".localized(),
    WORKED_HOURS: "Calculando horas trabajadas...".localized(),
    COMMON_NOVEDADES: "Procesando novedades comunes...".localized(),
    DAILY_DETAIL: "Calculando jornadas, ausencias y novedades...".localized(),
    SUMMARY: "Consolidando totales de la exportación...".localized(),
    FORMAT_EXPORT: "Preparando los datos para revisión...".localized(),
    SAVE_FILE: "Guardando el archivo de exportación...".localized(),
    PREPARING_EXPORT_DATA: "Preparando datos de exportación...".localized(),
    DATA_VALIDATION: "Validando datos de exportación...".localized(),
    FILE_GENERATION: "Generando archivo de exportación...".localized(),
    FILE_CREATED: "Archivo de exportación creado.".localized(),
};

const loadingTextByStatus: Record<string, string> = {
    CREATED: "La exportación está en cola para ser procesada...".localized(),
    RUNNING: "Procesando exportación...".localized(),
    PROCESSING: "Procesando exportación...".localized(),
    READY_FOR_REVIEW: "La exportación está lista para revisar.".localized(),
    EXPORT_GENERATED: "El archivo de exportación fue generado.".localized(),
    COMPLETED: "La exportación fue completada.".localized(),
    PARTIALLY_FAILED: "La exportación terminó con errores para revisar.".localized(),
    FAILED: "La exportación falló.".localized(),
};

const processMilestones = [
    { id: "EMPLOYEE_SCOPE", title: "Colaboradores".localized() },
    { id: "WORKED_HOURS", title: "Horas".localized() },
    { id: "COMMON_NOVEDADES", title: "Novedades".localized() },
    { id: "DAILY_DETAIL", title: "Detalle diario".localized() },
    { id: "SUMMARY", title: "Resumen".localized() },
    { id: "FORMAT_EXPORT", title: "Revisión".localized() },
];

const loadingDescriptionForProcess = (
    process: NovedadesExportServiceModel.ProcessRecord | undefined,
): string => {
    if (!process) {
        return "Cargando exportación...".localized();
    }

    if (process.currentStep && loadingTextByStep[process.currentStep]) {
        return loadingTextByStep[process.currentStep];
    }

    return loadingTextByStatus[process.status] ?? "Procesando exportación...".localized();
};

const currentMilestoneIdForProcess = (
    process: NovedadesExportServiceModel.ProcessRecord | undefined,
): string | undefined => {
    if (!process) {
        return undefined;
    }

    if (process.currentStep) {
        return process.currentStep;
    }

    if (process.status === "READY_FOR_REVIEW") {
        return "FORMAT_EXPORT";
    }

    if (process.status === "EXPORT_GENERATED" || process.status === "COMPLETED") {
        return "FILE_CREATED";
    }

    return "EMPLOYEE_SCOPE";
};

const ExportNovedadesModalScreen: React.FC<ExportNovedadesModalScreenProps> = ({
    isOpen,
    mode,
    orgID,
    service,
    usersService,
    process,
    onClose,
    onCreated,
    onProcessUpdated,
}) => {
    const [currentProcess, setCurrentProcess] =
        useState<NovedadesExportServiceModel.ProcessRecord | undefined>(process);
    const [isLoadingProcess, setIsLoadingProcess] = useState(false);
    const [isRestartingFailedProcess, setIsRestartingFailedProcess] = useState(false);
    const [processError, setProcessError] = useState<string | null>(null);
    const [failedProcessRestartError, setFailedProcessRestartError] = useState<string | null>(null);
    const [pollingVersion, setPollingVersion] = useState(0);
    const hasNotifiedCompletionRef = useRef(false);

    const processID = process?.processID;

    useEffect(() => {
        if (!isOpen || mode !== "view") {
            return;
        }

        setCurrentProcess(process);
        setProcessError(null);
        setFailedProcessRestartError(null);
        setIsRestartingFailedProcess(false);
        hasNotifiedCompletionRef.current = false;
    }, [isOpen, mode, process]);

    useEffect(() => {
        if (!isOpen || mode !== "view" || !processID) {
            return;
        }

        let cancelled = false;
        let timeoutID: number | undefined;
        let lastKnownStatus = process?.status;

        const scheduleNextPoll = () => {
            timeoutID = window.setTimeout(() => {
                void fetchProcess();
            }, 3000);
        };

        const fetchProcess = async () => {
            setIsLoadingProcess(true);
            setProcessError(null);

            try {
                const nextProcess = await service.getProcess(processID);
                if (cancelled) {
                    return;
                }

                setCurrentProcess(nextProcess);
                const previousStatus = lastKnownStatus;
                lastKnownStatus = nextProcess.status;
                if (isPollingCompleteStatus(nextProcess.status)) {
                    const observedCompletion = !isPollingCompleteStatus(previousStatus);
                    if (observedCompletion && !hasNotifiedCompletionRef.current) {
                        hasNotifiedCompletionRef.current = true;
                        NovedadesExportAnalyticsHelper.trackProcessCompletedPolling(
                            processAnalyticsPayload(nextProcess),
                        );
                        void onProcessUpdated?.();
                    }
                    return;
                }
                scheduleNextPoll();
            } catch (error) {
                console.error(error);
                if (cancelled) {
                    return;
                }
                setProcessError("No se pudo cargar el estado de la exportación.".localized());
                if (!isPollingCompleteStatus(lastKnownStatus)) {
                    scheduleNextPoll();
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingProcess(false);
                }
            }
        };

        void fetchProcess();

        return () => {
            cancelled = true;
            if (timeoutID != null) {
                window.clearTimeout(timeoutID);
            }
        };
    }, [isOpen, mode, onProcessUpdated, pollingVersion, process, processID, service]);

    const handleProcessRestarted = (restartedProcess: NovedadesExportServiceModel.ProcessRecord) => {
        setCurrentProcess(restartedProcess);
        setProcessError(null);
        setFailedProcessRestartError(null);
        hasNotifiedCompletionRef.current = false;
        setPollingVersion((version) => version + 1);
        void onProcessUpdated?.();
    };

    const handleFileGenerationStarted = () => {
        setProcessError(null);
        setFailedProcessRestartError(null);
        hasNotifiedCompletionRef.current = false;
        setPollingVersion((version) => version + 1);
    };

    const restartFailedProcess = async () => {
        if (!processID || isRestartingFailedProcess) {
            return;
        }

        setIsRestartingFailedProcess(true);
        setFailedProcessRestartError(null);
        NovedadesExportAnalyticsHelper.trackProcessingRestartSubmitted({
            processID,
            status: currentProcess?.status ?? process?.status ?? "FAILED",
            currentStep: currentProcess?.currentStep ?? process?.currentStep,
            origin: "failed_state",
        });

        try {
            const restartedProcess = await service.restartProcess(processID);
            NovedadesExportAnalyticsHelper.trackProcessingRestartSucceeded({
                processID: restartedProcess.processID,
                status: restartedProcess.status,
                currentStep: restartedProcess.currentStep,
                origin: "failed_state",
            });
            handleProcessRestarted(restartedProcess);
        } catch (error) {
            console.error(error);
            NovedadesExportAnalyticsHelper.trackProcessingRestartFailed({
                processID,
                status: currentProcess?.status ?? process?.status ?? "FAILED",
                currentStep: currentProcess?.currentStep ?? process?.currentStep,
                origin: "failed_state",
            });
            setFailedProcessRestartError("No se pudo reiniciar la exportación.".localized());
        } finally {
            setIsRestartingFailedProcess(false);
        }
    };

    const shouldShowDetail = isDetailReadyStatus(currentProcess);
    const modalTitle = mode === "new"
        ? titleByMode.new.localized()
        : currentProcess?.title || process?.title || titleByMode.view.localized();

    const renderViewMode = () => {
        if (!processID) {
            return (
                <QontyEmptyState
                    text={"No se seleccionó una exportación.".localized()}
                    darkMode={false}
                />
            );
        }

        if (processError) {
            return (
                <QontyEmptyState
                    text={processError}
                    darkMode={false}
                />
            );
        }

        if (currentProcess?.status === "FAILED") {
            return (
                <VerticalContainer 
                    justifyContent="center" 
                    width="100%" 
                    height="100%" 
                    alignment="center" gap={designSystemStyles.gapMd}
                >
                    <VerticalContainer 
                        justifyContent="center" 
                        alignment="center" gap={designSystemStyles.gapLg}
                    >
                        <QontyEmptyState
                            text={"La exportación falló.".localized()}
                            darkMode={false}
                        />
                        <HollowedButton
                            text={"Reiniciar exportación".localized()}
                            onClick={() => void restartFailedProcess()}
                            loading={isRestartingFailedProcess}
                            disabled={isRestartingFailedProcess}
                            width="220px"
                        />
                        {failedProcessRestartError && (
                            <Text
                                text={failedProcessRestartError}
                                color={designSystemStyles.colorError}
                            />
                        )}
                        </VerticalContainer>
                </VerticalContainer>
            );
        }

        if (!shouldShowDetail) {
            return (
                <VerticalContainer
                    justifyContent="center"
                    alignment="center"
                    width="100%"
                    height="100%"
                    gap={designSystemStyles.gapLg}
                >
                    <VerticalContainer width="100%" alignment="center">
                        <QontyLoading
                            withQonty={true}
                        />
                        <VerticalContainer width="88%" gap={designSystemStyles.gapMd} alignment="center">
                            <MilestoneSteps
                                ariaLabel={"Progreso de procesamiento de exportación".localized()}
                                currentMilestoneId={currentMilestoneIdForProcess(currentProcess)}
                                milestones={processMilestones}
                                description={loadingDescriptionForProcess(currentProcess)}
                            />
                        </VerticalContainer>
                    </VerticalContainer>
                </VerticalContainer>
            );
        }

        if (isLoadingProcess && !currentProcess) {
            return (
                <QontyLoading
                    text={"Cargando exportación...".localized()}
                    withQonty={true}
                />
            );
        }

        return (
            <ExportNovedadesDetailScreen
                process={currentProcess}
                service={service}
                onProcessRestarted={handleProcessRestarted}
                onFileGenerationStarted={handleFileGenerationStarted}
            />
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={modalTitle}
            width="1480px"
            height="920px"
            maxWidth="96vw"
            maxHeight="90vh"
        >
            {mode === "new" ? (
                <ExportNovedadesCreateScreen
                    isOpen={isOpen}
                    orgID={orgID}
                    service={service}
                    usersService={usersService}
                    onClose={onClose}
                    onCreated={onCreated}
                />
            ) : (
                renderViewMode()
            )}
        </Modal>
    );
};

export default ExportNovedadesModalScreen;
